import {createSelector} from "@reduxjs/toolkit";

import {buildCommentsForAttachments} from "@reva/shared/changelog";
import {type ActiveParameterValue, resolveActiveParameterValue} from "@reva/shared/parameter-resolution";
import type {
    AvmSpecSegment,
    RevaSpecObjectInputBlock,
    RevaSpecObjectInputColumn,
    RevaSpecObjectInputRow,
    RevaValueParameterType,
} from "@reva/shared/reva-spec";
import {hasSegmentConflict, resolveSegmentOrigin} from "@reva/shared/segment-description";
import type {RevaSpec} from "@reva/shared/spec-conversion";

import {checkDependsOn} from "../util/dependsExpressions";
import {currySelector, selectArg, selectArg1, selectArg2, selectArg3} from "../util/selectors";
import {
    type ObjectParamErrorsMap,
    selectAllCommentValues,
    selectAllDocProperties,
    selectAllEnergyParams,
    selectAllFirstFinalizeParams,
    selectAllIdpEnergyCertParams,
    selectAllImmodropParams,
    selectAllInitialValuationParams,
    selectAllInspectionDerivedParams,
    selectAllInspectionParams,
    selectAllMetaParams,
    selectAllParamErrors,
    selectAllParams,
    selectAllPlotParams,
    selectAllRegisterParams,
    selectAllValues,
    selectCommentForParam,
    selectExposeSegment,
    selectHasSpfTable,
    selectInitialValuationOutputParams,
    selectSegment,
    selectSingleAvmParam,
    selectSingleEnergyParam,
    selectSingleFirstFinalizeParam,
    selectSingleIdpEnergyCertParam,
    selectSingleImmodropParam,
    selectSingleInitialValuationParam,
    selectSingleInspectionDerivedParam,
    selectSingleInspectionParam,
    selectSingleParam,
    selectSingleParamError,
    selectSinglePlotParam,
    selectSingleRegisterParam,
} from "./objectParametersSlice";
import {selectSpec, selectSpecApiParamInputs} from "./specSlice";
import type {RevaRootState, RevaSelector} from "./store";
import type {ObjectDisplayStatus} from "./uiPreferencesSlice";
import {selectFlattenedIndicationData, selectSuggestedParameters} from "./valueIndicationSelectors";

/**
 * Resolve the active parameter based on the current state.
 * @param parameter parameter name to query
 * @param parameterType parameter type of the layer the user edited
 */
export const selectActiveValueForSingleParameter: RevaSelector<
    ActiveParameterValue,
    [parameter: string, parameterType: RevaValueParameterType]
> = createSelector(
    [
        selectSingleParam,
        (state: RevaRootState, parameter: string) => selectSingleAvmParam(state, parameter),
        (state: RevaRootState, parameter: string) => selectSingleInitialValuationParam(state, parameter),
        (state: RevaRootState, parameter: string) => selectSingleInspectionParam(state, parameter),
        (state: RevaRootState, parameter: string) => selectSingleInspectionDerivedParam(state, parameter),
        (state: RevaRootState, parameter: string) => selectSinglePlotParam(state, parameter),
        (state: RevaRootState, parameter: string) => selectSingleEnergyParam(state, parameter),
        (state: RevaRootState, parameter: string) => selectSingleIdpEnergyCertParam(state, parameter),
        (state: RevaRootState, parameter: string) => selectSingleRegisterParam(state, parameter),
        (state: RevaRootState, parameter: string) => selectSingleFirstFinalizeParam(state, parameter),
        // Das Segment führt die Exposé-Schicht nicht als gewöhnlichen Parameter (#19159), deshalb hier
        // der eigene Selektor -- so zählt das Exposé wie im Backend als vierte Segment-Quelle (#19444).
        (state: RevaRootState, parameter: string) =>
            parameter === "segment" ? selectExposeSegment(state) : selectSingleImmodropParam(state, parameter),
        selectCommentForParam,
        selectHasSpfTable,
        selectSegment,
        selectArg1<string>,
        selectArg2<RevaValueParameterType>,
    ],
    (
        reva,
        avm,
        initialValuation,
        inspection,
        inspectionDerived,
        plot,
        energy,
        idpEnergyCertificate,
        register,
        firstFinalize,
        immodrop,
        comment,
        hasSpfTable,
        segment,
        parameter,
        parameterType,
    ) => {
        const resolved = resolveActiveParameterValue(parameterType, {
            reva,
            avm,
            initialValuation,
            inspection,
            inspectionDerived,
            plot,
            energy,
            idpEnergyCertificate,
            register,
            firstFinalize,
            immodrop,
            // comment for parameter with selected parameterType
            comment,
            hasSpfTable,
            useConflictingValue: false,
            parameter,
        });

        if (parameter !== "segment") return resolved;

        // #19444: Wert, Herkunft und Konflikt des Segments kommen nicht aus der generischen Auflösung.
        // Der Wert ist der vom Backend berechnete -- auch im Konflikt, wo useConflictingValue: false
        // sonst ein leeres Feld zeigen würde, obwohl die ganze Maske mit diesem Wert rendert.
        // Herkunft und Konflikt folgen der Kette des Backends: eine andere Reihenfolge als ALL_SOURCES,
        // und nur die vier Schichten, die überhaupt Segment-Quellen sind.
        const segmentSources = {override: reva, initialValuation, inspection, immodrop};
        return {
            ...resolved,
            activeParameterValue: segment,
            activeParameterType: resolveSegmentOrigin(segment, segmentSources),
            hasConflict: hasSegmentConflict(segmentSources),
        };
    },
);

/**
 * Resolve the active parameter based on the current state (curried).
 */
export const selectActiveValueForSingleParameterCurried = currySelector(selectActiveValueForSingleParameter);

/**
 * Check if the current state is a Blue Horizon state
 */
export const selectIsBlueHorizon = createSelector(
    [selectAllInitialValuationParams, selectAllInspectionParams],
    (initialValuation, inspection) => {
        const isInitialValuationEmpty = Object.keys(initialValuation).length === 0;
        const isInspectionEmpty = Object.keys(inspection).length === 0;

        return !isInitialValuationEmpty || !isInspectionEmpty;
    },
);

/**
 * Check if an initial valuation is available
 */
export const selectHasInitialValuation = createSelector(
    [selectAllInitialValuationParams],
    (initialValuation) => Object.keys(initialValuation).length !== 0,
);

/**
 * Check if first finalize data is available
 */
export const selectHasFirstFinalizeData = createSelector(
    [selectAllFirstFinalizeParams],
    (firstFinalize) => Object.keys(firstFinalize).length !== 0,
);

/**
 * Check if a persistence layer contains values
 */
export const selectHasSubtabParams = createSelector(
    [
        selectAllRegisterParams,
        selectAllEnergyParams,
        selectAllIdpEnergyCertParams,
        selectAllPlotParams,
        selectAllInspectionDerivedParams,
        selectAllInspectionParams,
        selectAllInitialValuationParams,
        selectAllFirstFinalizeParams,
        selectAllImmodropParams,
    ],
    (
        register,
        energy,
        idpEnergyCertificate,
        plot,
        inspectionDerived,
        inspection,
        initialValuation,
        firstFinalize,
        immodrop,
    ) => {
        return {
            register: Object.keys(register).length !== 0,
            energy: Object.keys(energy).length !== 0,
            idpEnergyCertificate: Object.keys(idpEnergyCertificate).length !== 0,
            plot: Object.keys(plot).length !== 0,
            inspection: Object.keys(inspection).length !== 0 || Object.keys(inspectionDerived).length !== 0,
            inspectionDerived: Object.keys(inspectionDerived).length !== 0,
            initialValuation: Object.keys(initialValuation).length !== 0,
            firstFinalize: Object.keys(firstFinalize).length !== 0,
            immodrop: Object.keys(immodrop).length !== 0,
            reva: true,
        };
    },
);

/** Get selector for an error of a single/specific parameter. */
export const selectSingleParamErrorCurried = currySelector(selectSingleParamError);

const NO_FORCED_OPEN_META_PARAMS: string[] = [];

/**
 * Meta-Toggles, die zwangsweise aufgeklappt sind: Schlägt die AVM für einen Eingabeparameter einen
 * alternativen Wert vor (z.B. market_adjustment_factor), wird der Toggle, von dem seine Zeile abhängt
 * (meta_rav), in allen Ansichten geöffnet, damit die Eingabe erreichbar ist.
 */
export const selectForcedOpenMetaParams = createSelector([selectSpec, selectSuggestedParameters], (spec, suggested) => {
    if (!suggested.length || !Array.isArray(spec?.objectInputs)) return NO_FORCED_OPEN_META_PARAMS;
    const metaParams = spec.objectInputs
        .flatMap((page) => page.columns)
        .flatMap((column) => column.blocks)
        .flatMap((block) => block.rows)
        .filter(
            (row): row is RevaSpecObjectInputRow & {dependsOn: string} =>
                suggested.includes(row.parameter) && /^meta_\w+$/.test(row.dependsOn ?? ""),
        )
        .map((row) => row.dependsOn);
    return metaParams.length ? [...new Set(metaParams)] : NO_FORCED_OPEN_META_PARAMS;
});

/**
 * Segment, nach dem die sichtbaren Parameter gefiltert werden: das der Bewertung, oder ein als viertes
 * Argument übergebenes (der Exposé-Tab zeigt die Schicht im Segment des Exposés, #19159).
 */
const selectSegmentOrOverride = (
    state: RevaRootState,
    _page: unknown,
    _displayStatus: unknown,
    _customParams: unknown,
    segmentOverride?: AvmSpecSegment | null,
): AvmSpecSegment => segmentOverride ?? selectSegment(state);

/** Selector for current (filtered) object property columns. If data is not ready, returns empty array. */
export const selectVisibleObjectParameters: RevaSelector<
    RevaSpecObjectInputColumn[],
    [
        page: string,
        displayStatus: ObjectDisplayStatus,
        customParams?: Record<string, any> | null,
        segmentOverride?: AvmSpecSegment,
    ]
> = createSelector(
    [
        selectArg1<string>,
        selectArg2<ObjectDisplayStatus>,
        selectArg3<Record<string, any> | null | undefined>,
        selectSpec,
        selectSegmentOrOverride,
        selectAllParams,
        selectAllInitialValuationParams,
        selectAllInspectionParams,
        selectAllMetaParams,
        selectAllDocProperties,
        selectAllEnergyParams,
        selectAllIdpEnergyCertParams,
        selectAllRegisterParams,
        selectAllPlotParams,
        selectAllInspectionDerivedParams,
        selectAllFirstFinalizeParams,
        selectAllImmodropParams,
        selectAllParamErrors,
        selectHasSpfTable,
        selectForcedOpenMetaParams,
    ],
    /** Combination function; `customParams`, if given, filters just these parameters and not all. */
    (
        page: string,
        displayStatus: ObjectDisplayStatus,
        customParams: Record<string, any> | null = null,
        spec: RevaSpec | null,
        segment: AvmSpecSegment,
        allParams: Record<string, any>,
        initialValuationParams: Record<string, any>,
        inspectionParams: Record<string, any>,
        allMetaParams: Record<string, any>,
        allDocProperties: Record<string, any>,
        energyParams: Record<string, any>,
        idpEnergyCertParams: Record<string, any>,
        registerParams: Record<string, any>,
        plotParams: Record<string, any>,
        inspectionDerivedParams: Record<string, any>,
        firstFinalizeParams: Record<string, any>,
        immodropParams: Record<string, any>,
        paramErrors: ObjectParamErrorsMap,
        hasSpfTable: boolean,
        forcedOpenMetaParams: string[],
    ): RevaSpecObjectInputColumn[] => {
        if (!spec || !segment) return []; // return empty array if no data available
        const apiParamsInSegment = Object.keys(spec.apiParams?.VALUE?.[segment] ?? {});
        const anyParamValues: Record<string, any> =
            customParams != null
                ? {
                      ...customParams,
                      special_property_features:
                          customParams?.special_property_features_values || customParams?.special_property_features,
                  }
                : {
                      ...immodropParams,
                      ...initialValuationParams,
                      ...inspectionParams,
                      ...inspectionDerivedParams,
                      ...firstFinalizeParams,
                      special_property_features:
                          inspectionDerivedParams?.special_property_features_values ??
                          inspectionParams?.special_property_features_values ??
                          initialValuationParams?.special_property_features_values ??
                          immodropParams?.special_property_features_values,
                      ...allDocProperties,
                      ...plotParams,
                      ...idpEnergyCertParams,
                      ...energyParams,
                      ...registerParams,
                      ...allParams,
                      ...allMetaParams,
                  };

        /**
         * Check if an element is visible in the current segment, according to a segment filter
         * @param onlyVisibleInSegments segment filter to check
         * @return true if element should be visible
         */
        const isVisibleInSegment = (onlyVisibleInSegments?: AvmSpecSegment[] | null): boolean => {
            if (!Array.isArray(onlyVisibleInSegments) || !onlyVisibleInSegments.length) return true;
            return onlyVisibleInSegments.includes(segment);
        };

        /**
         * Check if a parameter has a non-empty value.
         * @param parameter name of the parameter to check
         * @return true if parameter has non-empty value
         */
        const hasNonEmptyValue = (parameter: string): boolean => {
            const paramValue = anyParamValues[parameter];
            return paramValue !== null && paramValue !== undefined;
        };

        /**
         * Check if a parameter has an error (from Inspection / Initial Valuation conflict or validation error), and should always be displayed.
         * Does not take into account, if the value is overwritten in REVA; because then the parameter is always displayed as well.
         * @param parameter name of the parameter to check
         * @return true if parameter has a conflict
         */
        const hasConflictOrError = (parameter: string): boolean => {
            const initialValuationDefined =
                initialValuationParams[parameter] !== null && typeof initialValuationParams[parameter] !== "undefined";
            const inspectionDefined =
                inspectionParams[parameter] !== null && typeof inspectionParams[parameter] !== "undefined";
            const hasConflict =
                initialValuationDefined &&
                inspectionDefined &&
                initialValuationParams[parameter] !== inspectionParams[parameter];
            return hasConflict || (paramErrors[parameter]?.length ?? 0) > 0;
        };

        const filterRows = (rows: RevaSpecObjectInputRow[]): RevaSpecObjectInputRow[] => {
            return rows.filter((row) => {
                // Magic "segment" input is always visible
                if (row.parameter === "segment") return true;

                // Never show a rows, that is not visible for the currently selected segment
                if (!isVisibleInSegment(row.onlyVisibleInSegments)) return false;

                // Never show a (non-meta) parameter, that is not defined for the current segment
                if (!row.details && !apiParamsInSegment.includes(row.parameter)) return false;

                //#18080: For pool and sauna: dont show if the spf table in inspectionDerived is not null
                if (hasSpfTable && (row.parameter === "prop_sauna" || row.parameter === "prop_pool")) {
                    return false;
                }

                // Zwangsweise geöffneter Toggle samt abhängigen Zeilen, unabhängig von der Ansicht
                if (
                    forcedOpenMetaParams.includes(row.parameter) ||
                    forcedOpenMetaParams.includes(row.dependsOn ?? "")
                ) {
                    return true;
                }

                // Always show inputs which have a conflict or validation error
                if (hasConflictOrError(row.parameter)) {
                    return true;
                }

                // Always show inputs with non-empty value (except toggle "meta" buttons)
                if (
                    hasNonEmptyValue(row.parameter) &&
                    (row.parameterType !== "META" || row.details?.type !== "toggle")
                ) {
                    return true;
                }

                if (displayStatus === "required") {
                    const rowParam = spec.apiParams?.VALUE?.[segment]?.[row.parameter];

                    const isRequired = rowParam?.required;
                    // Only show required params
                    return isRequired === true;
                } else if (displayStatus === "all") {
                    // Show all rows, independent of dependencies
                    return true;
                } /* displayStatus === "default" */ else {
                    // Only show row if the dependency (if any) is enabled
                    return checkDependsOn(anyParamValues, row.dependsOn ?? "");
                }
            });
        };

        const mapAndFilterBlocks = (blocks: RevaSpecObjectInputBlock[]): RevaSpecObjectInputBlock[] => {
            return blocks
                .map((block) => {
                    return {...block, rows: filterRows(block.rows)};
                })
                .filter((block) => block.rows.length > 0 && isVisibleInSegment(block.onlyVisibleInSegments));
        };
        const mapAndFilterCols = (columns: RevaSpecObjectInputColumn[]): RevaSpecObjectInputColumn[] => {
            return columns
                .map((column) => {
                    return {...column, blocks: mapAndFilterBlocks(column.blocks)};
                })
                .filter((column) => column.blocks.length > 0);
        };
        if (!Array.isArray(spec?.objectInputs)) return [];
        return mapAndFilterCols(
            spec.objectInputs
                .filter((p) => {
                    return p.key === page;
                })
                .flatMap((p) => p.columns) || [],
        );
    },
);

/** Selector (curried) for current (filtered) object property columns. If data is not ready, returns empty array. */
export const selectVisibleObjectParametersCurried = currySelector(selectVisibleObjectParameters);

/**
 * Check for conflicts in parameter values for the current segment.
 * @return true if there are conflicts, false otherwise
 */
export const selectHasAnyParamConflicts = createSelector(
    [
        selectSpec,
        selectSegment,
        selectAllParams,
        selectAllInitialValuationParams,
        selectAllInspectionParams,
        selectAllInspectionDerivedParams,
        selectAllPlotParams,
        selectAllRegisterParams,
        selectAllEnergyParams,
        selectAllIdpEnergyCertParams,
        selectAllFirstFinalizeParams,
        selectAllImmodropParams,
        selectHasSpfTable,
        selectExposeSegment,
    ],
    (
        spec,
        segment,
        allParams,
        initialValuationParams,
        inspectionParams,
        inspectionDerivedParams,
        plotParams,
        registerParams,
        energyParams,
        idpEnergyCertParams,
        firstFinalizeParams,
        immodropParams,
        hasSpfTable,
        exposeSegment,
    ): boolean => {
        if (!spec || !segment) {
            return false;
        }

        // #19444: Das Segment ist kein Eingabeparameter der Spec und würde von der Schleife unten nie
        // erfasst. Dabei ist es der folgenreichste Konflikt überhaupt -- es entscheidet, welche
        // Parameter es gibt. Solange ihn niemand aufgelöst hat, ist die Wahl der Kette eine Annahme,
        // und eingefroren werden soll nur, was ein Mensch bestätigt hat.
        if (
            hasSegmentConflict({
                override: allParams.segment,
                initialValuation: initialValuationParams.segment,
                inspection: inspectionParams.segment,
                immodrop: exposeSegment,
            })
        ) {
            return true;
        }

        const paramsInSegment = Object.entries(spec.apiParams?.VALUE?.[segment] || {});

        for (const [param] of paramsInSegment) {
            const reva = allParams[param];
            const initialValuation = initialValuationParams[param];
            const inspection = inspectionParams[param];
            const inspectionDerived = inspectionDerivedParams[param];
            const plot = plotParams[param];
            const register = registerParams[param];
            const energy = energyParams[param];
            const idpEnergyCertificate = idpEnergyCertParams[param];
            const firstFinalize = firstFinalizeParams[param];
            const immodrop = immodropParams[param];

            const resolved = resolveActiveParameterValue("AVM", {
                reva,
                initialValuation,
                inspection,
                inspectionDerived,
                plot,
                register,
                energy,
                idpEnergyCertificate,
                firstFinalize,
                immodrop,
                hasSpfTable,
                useConflictingValue: true,
                parameter: param,
            });

            if (resolved.hasConflict) {
                return true;
            }
        }

        return false;
    },
);

/** Get the current value of a parameter in the indication output. */
export const selectAssociatedOutputParamOutput = createSelector(
    [selectArg<string>, selectFlattenedIndicationData],
    (parameter, indicationData) => {
        if (!parameter || !indicationData) return null;

        return indicationData[parameter];
    },
);

/** Get the current value of a parameter in the indication output (curried). */
export const selectAssociatedOutputParamOutputCurried = currySelector(selectAssociatedOutputParamOutput);

export const selectCommentsForAttachments = createSelector(
    [
        selectSpecApiParamInputs,
        selectSegment,
        selectAllCommentValues,
        selectAllValues,
        selectInitialValuationOutputParams,
    ],
    (spec, segment, allComments, allValues, initialValuationOutputParams) =>
        buildCommentsForAttachments(spec, segment, allValues, allComments, initialValuationOutputParams),
);

/** Selector for parameters in input block object/register. */
export const selectRegisterInputs = createSelector([selectSpec], (specData) => {
    return specData?.objectInputs.filter((i) => i.key === "object/register");
});
